import { EyeSolid, GridOneSolid, UserSolid } from '@mynaui/icons-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import PortalTooltip from '@/components/PortalTooltip';
import type { CameraMode } from '@/lib/cameraMode';
import { cn } from '@/lib/utils';

/**
 * The viewer's camera control: one three-position switch instead of the two
 * independent toggles it replaced.
 *
 * The pair could never show the truth. Ortho forced an orbit camera regardless
 * of the third-person toggle, so the toggle sat there lit or unlit describing a
 * mode that was not in effect, and its button had to be disabled in ortho to
 * paper over it. Three segments, one pressed, no disabled state.
 */
const SEGMENTS = [
  { mode: 'FIRST_PERSON', icon: <EyeSolid />, labelKey: 'viewer3d.firstPersonFlying', shortcutKey: 'T' },
  { mode: 'THIRD_PERSON', icon: <UserSolid />, labelKey: 'viewer3d.thirdPersonFlying', shortcutKey: 'T' },
  { mode: 'ORTHO', icon: <GridOneSolid />, labelKey: 'viewer3d.orthographicCamera', shortcutKey: 'O' },
] as const satisfies readonly { mode: CameraMode; icon: React.ReactNode; labelKey: string; shortcutKey: string }[];

export const CameraModeSwitch = ({
  mode,
  onChange,
  vertical,
}: {
  mode: CameraMode;
  onChange: (mode: CameraMode) => void;
  /** The action bar runs down the right edge (`actionPosition === 'RIGHT'`). */
  vertical?: boolean;
}) => {
  const { t } = useTranslation('project');
  const id = useId();

  return (
    <div
      data-testid='camera-mode-switch'
      className={cn(
        'flex rounded-md border overflow-hidden backdrop-blur-md',
        'bg-white/70 border-slate-200/70 dark:bg-stone-900/80 dark:border-stone-700/70',
        vertical ? 'flex-col' : 'flex-row',
      )}>
      {SEGMENTS.map((segment, i) => {
        const active = segment.mode === mode;
        const tooltipId = `${id}-${segment.mode}`;
        return (
          <div key={segment.mode} className='contents'>
            <button
              data-camera-mode={segment.mode}
              data-tooltip-id={tooltipId}
              aria-pressed={active}
              aria-label={t(segment.labelKey)}
              // Re-picking the active mode would run a camera transition for
              // nothing; the switch is a radio group, not three toggles.
              onClick={active ? undefined : () => onChange(segment.mode)}
              className={cn(
                'size-8 flex items-center justify-center [&>svg]:size-4 [&>svg]:stroke-2',
                i > 0 && (vertical ? 'border-t border-slate-200/70 dark:border-stone-700/70' : 'border-l border-slate-200/70 dark:border-stone-700/70'),
                active
                  ? 'bg-slate-800/80 text-white hover:bg-slate-800 active:bg-slate-900 dark:bg-white dark:text-stone-700'
                  : 'text-slate-700 hover:bg-slate-50 active:bg-slate-100 dark:text-stone-100 dark:hover:bg-stone-800',
              )}>
              {segment.icon}
            </button>
            <PortalTooltip id={tooltipId} delayHide={0} delayShow={120}>
              <span className='flex items-center gap-2'>
                {t(segment.labelKey)}
                <kbd className='bg-slate-800/70 dark:bg-stone-900 font-medium font-mono px-1.5 py-0.5 rounded text-[10px]'>
                  {segment.shortcutKey}
                </kbd>
              </span>
            </PortalTooltip>
          </div>
        );
      })}
    </div>
  );
};
